import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Fab,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Modal,
  Typography,
  useTheme,
  Alert,
  Chip,
} from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { ScreenWidthContext } from "../contexts/ScreenWidthContext";
import useAuth from "../hooks/useAuth";
import { auth0Client } from "../contexts/auth0Client";
import useOrgSelectionModal from "../hooks/useOrgSelectionModal";
import useSettings from "../hooks/useSettings";
import {
  getCurrentOrganization,
  getUserRoles,
  setUserOrganization,
} from "../slices/user";
import { useDispatch, useSelector } from "../store";
import { Organization } from "../types/organization";
import { hasSubscriptionManagerRole } from "../utils/roleUtils";

const ModalCloseButton = ({ onClick }: { onClick: () => void }) => {
  const theme = useTheme();
  return (
    <Fab
      onClick={onClick}
      size="small"
      style={{
        position: "absolute",
        top: -20,
        right: -20,
        backgroundColor: theme.palette.background.paper,
        border: "2px solid #000",
        color: theme.palette.text.primary,
      }}
    >
      <CloseIcon fontSize="small" />
    </Fab>
  );
};

function OrgSelectionModal() {
  const { settings, saveSettings } = useSettings();
  const dispatch = useDispatch();
  const { organizations, isAuthenticated, logout } = useAuth();
  const { isOpen, closeOrgSelectionModal } = useOrgSelectionModal();
  const [selectedOrg, setSelectedOrg] = useState<Organization>();
  const [restrictionError, setRestrictionError] = useState<string | null>(null);
  const navigate = useNavigate();
  const screenWidth = useContext(ScreenWidthContext);

  const user = useSelector((state) => state.user.profile);

  const handleOrgClick = async (org: Organization) => {
    setSelectedOrg(org);
    setRestrictionError(null);

    const isRestrictedStatus =
      org?.status === "suspended" ||
      org?.status === "unpaid" ||
      org?.status === "cancelled" ||
      org?.active === false;

    if (isRestrictedStatus) {
      try {
        const claims = await auth0Client?.getIdTokenClaims();
        const roles =
          claims?.["https://www.medialhealth.com/roles"]?.[org.authId] || [];
        const isSubscriptionManager = hasSubscriptionManagerRole(roles);

        if (!isSubscriptionManager) {
          const isCancelled = org?.status === "cancelled";
          const msg = isCancelled
            ? "This organization's subscription has been cancelled and you can no longer use the application. Please contact your Subscription Manager to reactivate access."
            : "This organization has been restricted due to unpaid invoices. Only Subscription Managers can log in to manage billing.";
          setRestrictionError(msg);
        }
      } catch (err) {
        console.error("Error checking roles for restricted org:", err);
      }
    }
  };

  const getRoleAndOrg = () => {
    if (selectedOrg && user) {
      dispatch(getUserRoles(selectedOrg.authId));
      dispatch(getCurrentOrganization(selectedOrg.id));
    }
  };

  const saveOrganization = () => {
    if (restrictionError) return;
    getRoleAndOrg();
    const newSettings = { ...settings, selectedOrganization: selectedOrg };
    dispatch(setUserOrganization(selectedOrg));
    saveSettings(newSettings);
    setSelectedOrg(undefined);
    closeOrgSelectionModal();

    navigate(`/home`);
    window.location.reload(); // This aborts fetch requests in process and throws an error.
  };

  const canCloseModal = (): boolean => {
    if (settings?.selectedOrganization?.id && organizations?.length) {
      const org = organizations.find(
        (organization: Organization) =>
          organization.id === settings.selectedOrganization.id,
      );
      return org?.id === settings.selectedOrganization.id;
    }
    return false;
  };

  return (
    <div>
      <Modal
        open={isOpen && isAuthenticated}
        aria-labelledby="organization-selection-modal"
        aria-describedby="organization-selection-modal"
        onClose={canCloseModal() ? closeOrgSelectionModal : null}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: screenWidth < 700 ? 400 : 500,
            maxHeight: "90%",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 10,
            borderRadius: 1,
            p: 2,
          }}
        >
          {canCloseModal() ? (
            <ModalCloseButton onClick={closeOrgSelectionModal} />
          ) : null}
          <Grid
            container
            sx={{
              height: "100%",
              overflow: "auto",
            }}
          >
            <Grid container>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Select Your Medical Facility
              </Typography>
              <Typography id="modal-modal-title">
                The selected medical facility will be the institution at which
                the patient is currently receiving care.
              </Typography>
            </Grid>
            <Divider style={{ color: "grey", width: "100%", marginTop: 15 }} />
            <Grid container>
              <Grid
                container
                item
                xs={12}
                style={{
                  maxHeight: 500,
                  overflow: "auto",
                }}
              >
                <Grid item md={12} xs={12}>
                  <List id="org-list">
                    {organizations?.map((org, i) => (
                      <ListItem
                        disablePadding
                        divider={i < organizations.length - 1}
                        key={org.id}
                        role="listitem"
                        data-testid="org-list-item"
                      >
                        <ListItemButton
                          selected={selectedOrg?.id === org.id}
                          onClick={() => {
                            handleOrgClick(org);
                          }}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={org.branding ? org.branding.logo_url : null}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            disableTypography
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  color="textPrimary"
                                  variant="subtitle2"
                                >
                                  {org.displayName}
                                </Typography>
                                {(org.status === "suspended" ||
                                  org.status === "unpaid" ||
                                  org.status === "past_due" ||
                                  org.status === "cancelled" ||
                                  org.active === false) && (
                                  <Chip
                                    label={
                                      org.status === "cancelled"
                                        ? "CANCELLED"
                                        : "RESTRICTED"
                                    }
                                    size="small"
                                    color="error"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography color="textSecondary" variant="body2">
                                {org.name}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Grid>
            {restrictionError && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Alert severity="error">{restrictionError}</Alert>
              </Grid>
            )}
            <Divider
              style={{
                color: "grey",
                width: "100%",
                marginBottom: 10,
              }}
            />
            <Grid container justifyContent="flex-end">
              <Box justifyContent="flex-end">
                <Grid item xs={12}>
                  <Button variant="outlined" color="secondary" onClick={logout}>
                    Logout
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    style={{ marginLeft: 10 }}
                    disabled={!selectedOrg?.id || !!restrictionError}
                    onClick={() => {
                      saveOrganization();
                    }}
                  >
                    Continue
                  </Button>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
}

export default OrgSelectionModal;
